/** Banco de provas: roda cada modelo AQUI DENTRO e mede o que ele custa.
 *
 * A diferença para medir no navegador é o que decide a utilidade disto. No
 * Chrome, cada aba é um processo: se estoura, o Android mata a aba. No WebView
 * o conteúdo roda dentro do processo do app — se estoura, morre o app inteiro,
 * e o orçamento é dividido com a interface, o banco local e o cache de imagens.
 * Um número medido no navegador é o teto otimista; este é o que vale.
 *
 * A instrumentação troca `WebAssembly.Memory` por uma subclasse que guarda cada
 * instância criada, e soma o `byteLength` de todas. É o único jeito de ver a
 * memória do modelo: ela mora no heap do WASM, e `performance.memory` só
 * enxerga o heap do JavaScript, onde o modelo não põe quase nada.
 */

import { RECURSOS, marcarVeredito, type RecursoId } from "./inferencia.js";

export type ProvaModelo = {
  id: string;
  tarefa: string;
  nome: string;
  /** O dtype que o wuweiAgent usa para ESTE modelo. O padrão dele é q8, mas
   *  `DTYPE_OVERRIDES` em `wuweiAgent/src/tools/_shared.ts` força fp32 nos
   *  Whisper e no legendador de imagem. São 4 bytes por peso contra 1, e em
   *  whisper-base isso mede 993 MB contra 653 MB — medir q8 quando o app roda
   *  fp32 é medir outro programa. */
  dtype: "q8" | "fp32" | "q4" | "q4f16" | "int8";
  /** Heap WASM medido no container Linux, em MB. Só para comparação, e só onde
   *  foi medido de verdade — o teto de cgroup NÃO serve aqui, porque carrega
   *  ~400 MB de Chrome e servidor junto. */
  refMb?: number;
  /** Piso conhecido, em MB: o peso do arquivo de pesos que o modelo baixa.
   *
   *  Existe porque um modelo novo entra na lista sem nunca ter sido medido, e
   *  `refMb` só aceita medição — escrever estimativa ali apagaria a diferença
   *  entre "medimos" e "achamos". O heap real é sempre MAIOR que este número
   *  (a engine e o cache de atenção entram por cima), então ele serve para
   *  ordenar e para recusar, nunca para aprovar. */
  pisoMb?: number;
  audio?: boolean;
  imagem?: boolean;
  /** Texto que a pessoa vê e que a inferência precisa completar. Só para
   *  `text-generation`, onde a entrada padrão de frase solta não serve. */
  prompt?: string;
  /** Custa, no container, mais do que o WebView do app oferece. Fica fora da
   *  bateria automática — o desfecho esperado é o app fechar. */
  perigoso?: boolean;
  /** Por que é perigoso, quando a frase padrão ("custa ~N MB onde foi medido")
   *  não é verdade — caso de quem entrou pelo piso e não pela medição. */
  motivoPerigo?: string;
};

/** O custo que se conhece de uma prova ANTES de rodá-la: a medição quando
 *  existe, o piso do arquivo quando não existe.
 *
 *  Serve só para ordenar e para recusar. Aprovar exige o número medido aqui. */
export function custoConhecidoMb(p: ProvaModelo): number {
  return p.refMb ?? p.pisoMb ?? 0;
}

/** Custo medido no container, em MB de heap WASM. Serve para ORDENAR a lista
 *  do mais barato ao mais caro — quem testa descobre onde está o limite antes
 *  de bater nele, em vez de derrubar o app no primeiro toque. */
export const PROVAS: ProvaModelo[] = [
  { id: "Xenova/all-MiniLM-L6-v2", tarefa: "feature-extraction", nome: "Busca semântica", dtype: "q8", refMb: 76 },
  { id: "jdp8/vit-age-classifier", tarefa: "image-classification", nome: "Estimar idade", dtype: "q8", refMb: 229, imagem: true },
  { id: "Xenova/facial_emotions_image_detection", tarefa: "image-classification", nome: "Emoção facial", dtype: "q8", refMb: 229, imagem: true },
  { id: "onnx-community/gender-classification-ONNX", tarefa: "image-classification", nome: "Gênero percebido", dtype: "q8", refMb: 229, imagem: true },
  { id: "Xenova/bert-base-NER", tarefa: "token-classification", nome: "Nomes e lugares", dtype: "q8", refMb: 280 },
  { id: "Xenova/whisper-tiny", tarefa: "automatic-speech-recognition", nome: "Transcrever em q8", dtype: "q8", refMb: 376, audio: true },
  { id: "Xenova/clip-vit-base-patch32", tarefa: "zero-shot-image-classification", nome: "Imagem ↔ texto", dtype: "q8", refMb: 386, imagem: true },
  { id: "Xenova/distilbert-base-multilingual-cased-sentiments-student", tarefa: "text-classification", nome: "Sentimento", dtype: "q8", refMb: 391 },
  { id: "Xenova/vit-gpt2-image-captioning", tarefa: "image-to-text", nome: "Legendar imagem em q8", dtype: "q8", refMb: 523, imagem: true },
  { id: "Xenova/detr-resnet-50", tarefa: "object-detection", nome: "Detectar objetos", dtype: "q8", refMb: 544, imagem: true },
  { id: "Xenova/whisper-tiny", tarefa: "automatic-speech-recognition", nome: "Transcrever em fp32 (como o agente fazia)", dtype: "fp32", refMb: 590, audio: true, perigoso: true },
  { id: "Xenova/distilbart-cnn-6-6", tarefa: "summarization", nome: "Resumo", dtype: "q8", refMb: 724 },
  { id: "Xenova/mDeBERTa-v3-base-xnli-multilingual-nli-2mil7", tarefa: "zero-shot-classification", nome: "Classificar texto sem treino", dtype: "q8", refMb: 922 },
  { id: "Xenova/vit-gpt2-image-captioning", tarefa: "image-to-text", nome: "Legendar imagem em fp32 (como o agente fazia)", dtype: "fp32", refMb: 2153, imagem: true, perigoso: true },
  { id: "Xenova/nllb-200-distilled-600M", tarefa: "translation", nome: "Traduzir", dtype: "q8", refMb: 2796, perigoso: true },

  // Gemma 3 1B: o primeiro modelo de linguagem geral da lista. Tudo acima é
  // classificador ou tradutor — dão uma resposta e acabam. Este escreve texto
  // livre, então o custo não para no peso: o cache de atenção cresce com o que
  // já foi gerado, e é por isso que ele entra pelo `pisoMb` e não pelo `refMb`.
  //
  // 819 MB é o `model_q4.onnx_data` do repositório, conferido em 8/ago/2026.
  // É piso, não medição: os pesos são o mínimo que precisa estar na memória
  // para a primeira palavra sair. O orçamento do WebView é 1024 MB para o app
  // inteiro — interface, banco e cache de imagens no mesmo bolso. Por isso
  // entra `perigoso`: fica fora da bateria automática, e quem quiser o número
  // real mede na página dedicada, onde a perda esperada é a aba e não o app.
  {
    id: "onnx-community/gemma-3-1b-it-ONNX",
    tarefa: "text-generation",
    nome: "Conversar (Gemma 3 1B)",
    dtype: "q4",
    pisoMb: 819,
    prompt: "Em uma frase, o que é uma rede local?",
    perigoso: true,
    // Rodado na bancada dedicada em 8/ago/2026, e o desfecho fecha a questão
    // para o caminho de CPU: o runtime aborta na criação da sessão. O controle
    // da mesma bancada explica por quê — um modelo de 0,5B pelo mesmo caminho
    // ocupou 3689 MB dos 4096 MB que o WebAssembly de 32 bits endereça.
    motivoPerigo:
      "medido na página dedicada: o runtime WASM aborta ao criar a sessão. Um modelo de " +
      "metade do tamanho já ocupa 3689 MB dos 4096 MB que o WebAssembly de 32 bits endereça — " +
      "não é falta de RAM, é falta de espaço de endereço",
  },
];


const LIB = "https://cdn.jsdelivr.net/npm/@huggingface/transformers@3.8.1";

/** Instalado uma vez só, e antes de qualquer import da biblioteca — a memória
 *  é criada no instante em que o módulo WASM é instanciado. */
const memorias: WebAssembly.Memory[] = [];
let vigiando = false;
function vigiarMemoria(): void {
  if (vigiando) return;
  const Original = WebAssembly.Memory;
  class Vigiada extends Original {
    constructor(d: WebAssembly.MemoryDescriptor) {
      super(d);
      memorias.push(this as unknown as WebAssembly.Memory);
    }
  }
  (WebAssembly as unknown as { Memory: unknown }).Memory = Vigiada;
  vigiando = true;
}

export function heapWasmMb(): number {
  return Math.round(memorias.reduce((s, m) => s + m.buffer.byteLength, 0) / 1048576);
}

function entradaDe(p: ProvaModelo): unknown {
  if (p.prompt) return p.prompt;
  if (p.audio) {
    // 8s de ruído com estrutura. Silêncio puro faz o decoder parar cedo e o
    // teste mede menos memória do que o uso real.
    const a = new Float32Array(16000 * 8);
    for (let i = 0; i < a.length; i++) {
      a[i] = Math.sin(i / 12) * 0.28 + Math.sin(i / 3.1) * 0.12;
    }
    return a;
  }
  if (p.imagem) {
    const cv = document.createElement("canvas");
    cv.width = 224;
    cv.height = 224;
    const c = cv.getContext("2d")!;
    const g = c.createLinearGradient(0, 0, 224, 224);
    g.addColorStop(0, "#204080");
    g.addColorStop(1, "#c04020");
    c.fillStyle = g;
    c.fillRect(0, 0, 224, 224);
    return cv.toDataURL("image/png");
  }
  return "Maria Silva viajou de São Paulo para Lisboa em março e gostou muito da viagem.";
}

/** O segundo argumento do pipeline, quando a tarefa exige um.
 *
 *  Sem isto, tradução e classificação sem treino falham com erro de formato e
 *  a linha sai como "falhou" sem nunca ter medido memória — que foi o defeito
 *  que quase entrou nos specs de imagem. */
function extraDe(tarefa: string): unknown {
  switch (tarefa) {
    case "feature-extraction":
      return { pooling: "mean", normalize: true };
    case "zero-shot-image-classification":
      return ["gato", "cachorro", "carro"];
    case "zero-shot-classification":
      return ["reclamação", "elogio", "dúvida"];
    case "translation":
      return { src_lang: "por_Latn", tgt_lang: "eng_Latn" };
    // Sem um teto explícito o modelo de linguagem escreve até o fim do próprio
    // orçamento de tokens, e a medição vira refém do tamanho da resposta. 32
    // tokens bastam para o cache de atenção existir e o custo aparecer.
    case "text-generation":
      return { max_new_tokens: 32, do_sample: false };
    default:
      return null;
  }
}

export type ResultadoProva = {
  estado: "ok" | "erro";
  /** Pico do heap WASM no worker, do zero até o fim da inferência. Como o
   *  worker nasce limpo e morre em seguida, este é o custo do modelo sozinho —
   *  não o que ele acrescentou sobre os anteriores. */
  custoMb: number;
  /** Quanto já havia sido alocado só para carregar, antes de inferir. */
  carregarMb: number;
  carregarSeg: number;
  totalSeg: number;
  detalhe: string;
};

/** O worker: instrumenta a própria memória, roda UM modelo e devolve o custo.
 *
 *  Precisa ser um worker, e não uma chamada na página, por um motivo medido: o
 *  heap do WASM é reaproveitado entre modelos e nunca encolhe (WebAssembly tem
 *  `memory.grow()`, não tem `shrink()`). Medindo tudo na mesma página, o
 *  segundo modelo cabe no espaço que o primeiro abriu e aparece custando quase
 *  nada — foi assim que a primeira versão desta tela reportou "Nomes e lugares:
 *  0 MB". Cada worker começa com heap zerado, e `terminate()` devolve tudo. */
const CORPO_WORKER = `
self.onmessage = async (e) => {
  const { id, tarefa, entrada, extra, lib, dtype } = e.data;
  const memorias = [];
  const Original = WebAssembly.Memory;
  WebAssembly.Memory = class extends Original {
    constructor(d) { super(d); memorias.push(this); }
  };
  const mb = () => Math.round(memorias.reduce((s, m) => s + m.buffer.byteLength, 0) / 1048576);
  const t0 = performance.now();
  const seg = () => Number(((performance.now() - t0) / 1000).toFixed(1));
  try {
    const mod = await import(lib);
    const pipe = await mod.pipeline(tarefa, id, {
      dtype: dtype,
      session_options: {
        graphOptimizationLevel: "disabled",
        enableCpuMemArena: false,
        enableMemPattern: false,
        executionMode: "sequential",
      },
    });
    const carregarSeg = seg();
    const carregarMb = mb();
    self.postMessage({ tipo: "andar", texto: "carregou em " + carregarSeg + "s (" + carregarMb + " MB) — inferindo…" });
    const ent = tarefa === "automatic-speech-recognition" ? new Float32Array(entrada) : entrada;
    const saida = extra
      ? await pipe(ent, extra)
      : await pipe(ent);
    self.postMessage({
      tipo: "fim", estado: "ok", custoMb: mb(), carregarMb,
      carregarSeg, totalSeg: seg(),
      detalhe: JSON.stringify(saida).slice(0, 80),
    });
  } catch (err) {
    self.postMessage({
      tipo: "fim", estado: "erro", custoMb: mb(), carregarMb: 0,
      carregarSeg: 0, totalSeg: seg(),
      detalhe: String(err && err.message || err).slice(0, 160),
    });
  }
};
`;

/** Chave do rastro deixado antes de cada teste.
 *
 *  Quando o modelo estoura a memória do WebView, o Android mata o app inteiro:
 *  não há exceção, não há `onerror`, não há tempo de gravar resultado. O teste
 *  que matou o app é justamente o único que não consegue se reportar. Gravando
 *  a intenção ANTES de começar, o próximo carregamento encontra o rastro e
 *  sabe quem foi. */
const CHAVE_EM_CURSO = "wuwei.prova.emcurso";

/** O que ficou marcado como "em curso" e nunca terminou — ou seja, o que
 *  derrubou o app na sessão anterior. Ler já limpa: o rastro serve uma vez. */
export function provaQueDerrubou(): {
  id?: string;
  nome: string;
  dtype: string;
  quando: string;
} | null {
  const cru = localStorage.getItem(CHAVE_EM_CURSO);
  if (!cru) return null;
  localStorage.removeItem(CHAVE_EM_CURSO);
  try {
    return JSON.parse(cru);
  } catch {
    return null;
  }
}

export async function rodarProva(
  p: ProvaModelo,
  aoAndar: (texto: string) => void,
): Promise<ResultadoProva> {
  localStorage.setItem(
    CHAVE_EM_CURSO,
    JSON.stringify({ nome: p.nome, dtype: p.dtype, id: p.id, quando: new Date().toISOString() }),
  );
  aoAndar("baixando e carregando…");
  const url = URL.createObjectURL(new Blob([CORPO_WORKER], { type: "text/javascript" }));
  const w = new Worker(url, { type: "module" });
  const t0 = performance.now();
  try {
    const r = await new Promise<ResultadoProva>((resolve) => {
      w.onmessage = (ev) => {
        const d = ev.data;
        if (d?.tipo === "andar") {
          aoAndar(d.texto);
          return;
        }
        resolve(d as ResultadoProva);
      };
      w.onerror = (ev) =>
        resolve({
          estado: "erro",
          custoMb: 0,
          carregarMb: 0,
          carregarSeg: 0,
          totalSeg: Number(((performance.now() - t0) / 1000).toFixed(1)),
          // Um worker que morre por falta de memória cai aqui sem mensagem
          // útil: o Android matou o processo antes de haver exceção.
          detalhe: ev.message || "o worker morreu sem mensagem — provável falta de memória",
        });
      w.postMessage({
        id: p.id,
        tarefa: p.tarefa,
        dtype: p.dtype,
        entrada: entradaDe(p),
        extra: extraDe(p.tarefa),
        lib: LIB,
      });
    });
    return r;
  } finally {
    // Isto é o que torna a próxima medição limpa.
    w.terminate();
    URL.revokeObjectURL(url);
    // Chegou aqui => não derrubou o app; o rastro não serve mais.
    localStorage.removeItem(CHAVE_EM_CURSO);
    // O terminate devolve a memória ao processo, mas o Android não a recicla
    // no mesmo instante. Encadear testes sem pausa foi o que transformou uma
    // bateria inteira em app fechado.
    await new Promise((r) => setTimeout(r, 2500));
  }
}

/* ── Bateria automática ─────────────────────────────────────────────────────
 *
 * A bateria manual pedia um toque por modelo e não tinha freio: nada impedia
 * de começar pelo mais pesado, e foi assim que um usuário perdeu tudo que já
 * tinha medido — o app fechou no meio e levou junto os resultados que só
 * existiam na tela.
 *
 * O desenho aqui tem três defesas, e nenhuma delas é `try/catch`. Estouro de
 * memória no WebView não lança exceção: o Android encerra o processo, e o
 * `catch` que deveria pegar o erro morre junto com o resto.
 *
 *  1. Ordem crescente e parada na primeira falha. O que vem depois é mais
 *     pesado; se o de 523 MB não coube, tentar o de 724 MB é só uma forma mais
 *     cara de descobrir a mesma coisa.
 *  2. Teto medido com folga. Não se tenta modelo cujo custo conhecido passe de
 *     `FOLGA_DO_TETO` do maior bloco que esta aba conseguiu reservar de fato.
 *     Sem esse número medido a bateria se recusa a começar — chutar a margem
 *     foi exatamente o erro que fechou o app da primeira vez.
 *  3. Rastro antes de cada tentativa (`CHAVE_EM_CURSO`). É a defesa de última
 *     instância: se apesar de tudo o app morrer, na volta se sabe quem o matou.
 *
 * O worker não entra nesta lista de propósito. Ele isola sujeira entre modelos
 * — cada um nasce com heap limpo — mas divide o processo com o app: pedido
 * grande demais mata os dois juntos. Worker protege a medição, não o app.
 */

/** Quanto do teto medido a bateria se permite pedir.
 *
 *  Não é o limite do aparelho, é o ponto em que se para antes dele. Bater no
 *  teto não dá erro tratável: dá app fechado. */
export const FOLGA_DO_TETO = 0.75;

/** A chave de ativação que esta prova governa, ou null quando ela não governa
 *  nenhuma.
 *
 *  Casa pelo id do modelo, e não por uma tabela escrita à mão, porque a tabela
 *  envelhece calada: trocar o modelo de um recurso deixaria o veredito antigo
 *  valendo para um modelo que ninguém mediu.
 *
 *  As provas `perigoso` ficam de fora mesmo quando o id casa. São as variantes
 *  fp32 de modelos que o app usa em q8 — deixar a medição de 2153 MB do
 *  legendador fp32 condenar o legendador q8, de 523 MB, seria reprovar um
 *  recurso pelo custo de outro. */
export function recursoDaProva(p: ProvaModelo): RecursoId | null {
  if (p.perigoso) return null;
  return RECURSOS.find((r) => r.modelo === p.id)?.id ?? null;
}

export type PassoBateria = {
  prova: ProvaModelo;
  recurso: RecursoId | null;
  /** false quando o custo já conhecido passa da folga: não se tenta. */
  rodar: boolean;
  /** Por que não se tenta. Vazio quando se tenta. */
  motivo: string;
};

export type PlanoBateria = {
  /** Preenchida quando não dá para rodar nada com segurança. */
  recusa: string | null;
  /** O que a bateria se permite pedir, em MB. */
  limiteMb: number | null;
  /** Do mais leve ao mais pesado. */
  passos: PassoBateria[];
};

/** O que a bateria vai (e não vai) tentar, decidido ANTES de tocar em nada.
 *
 *  É função pura de propósito: dá para conferir o plano — e testá-lo — sem
 *  baixar um único modelo. */
export function planejarBateria(tetoMb: number | null): PlanoBateria {
  const ordem = [...PROVAS].sort((a, b) => custoConhecidoMb(a) - custoConhecidoMb(b));
  if (tetoMb == null || tetoMb <= 0) {
    return {
      recusa:
        "primeiro meça o maior bloco que esta aba consegue reservar — sem esse número " +
        "não há margem de segurança, e tentar sem margem é como o app fechou da última vez",
      limiteMb: null,
      passos: ordem.map((prova) => ({
        prova,
        recurso: recursoDaProva(prova),
        rodar: false,
        motivo: "teto do aparelho ainda não medido",
      })),
    };
  }
  const limiteMb = Math.floor(tetoMb * FOLGA_DO_TETO);
  return {
    recusa: null,
    limiteMb,
    passos: ordem.map((prova) => {
      const recurso = recursoDaProva(prova);
      if (prova.perigoso) {
        return {
          prova,
          recurso,
          rodar: false,
          motivo:
            prova.motivoPerigo ??
            `custa ~${prova.refMb} MB onde foi medido — o desfecho esperado é fechar o app`,
        };
      }
      if (prova.refMb != null && prova.refMb > limiteMb) {
        return {
          prova,
          recurso,
          rodar: false,
          motivo:
            `custa ~${prova.refMb} MB e a bateria só tenta até ${limiteMb} MB ` +
            `(${Math.round(FOLGA_DO_TETO * 100)}% dos ${tetoMb} MB que esta aba reservou)`,
        };
      }
      // Nunca medido: o piso do arquivo é o que se sabe, e ele já basta para
      // recusar. Aprovar por piso seria aprovar pelo número menor.
      if (prova.refMb == null && prova.pisoMb != null && prova.pisoMb > limiteMb) {
        return {
          prova,
          recurso,
          rodar: false,
          motivo:
            `só os pesos já são ~${prova.pisoMb} MB e a bateria só tenta até ${limiteMb} MB ` +
            `(${Math.round(FOLGA_DO_TETO * 100)}% dos ${tetoMb} MB que esta aba reservou)`,
        };
      }
      return { prova, recurso, rodar: true, motivo: "" };
    }),
  };
}

/** Se o desfecho tem cara de falta de memória.
 *
 *  Separa os dois motivos de falha que exigem reações opostas. Sem memória, o
 *  que vem depois é mais pesado e está condenado junto — marcar "não cabe" é
 *  uma inferência sólida. Erro de rede ou de formato não diz nada sobre o
 *  tamanho do aparelho, e condenar os seguintes por causa dele seria inventar
 *  medição. */
export function pareceFaltaDeMemoria(detalhe: string): boolean {
  return /mem[óo]ria|memory|allocat|RangeError|sem mensagem|out of|Aborted|OOM|grow/i.test(
    detalhe,
  );
}

export type AndamentoBateria = {
  indice: number;
  total: number;
  prova: ProvaModelo;
  recurso: RecursoId | null;
  texto: string;
  estado: EstadoDoPasso;
};

export type EstadoDoPasso = "andando" | "cabe" | "naocabe" | "naotestado";

/** Roda a bateria inteira, do mais leve ao mais pesado, gravando o veredito de
 *  cada recurso ANTES de passar ao seguinte.
 *
 *  Grava um a um, e não tudo no fim, porque o fim pode não chegar. Se o modelo
 *  seguinte fechar o app, o que já se descobriu precisa ter sobrevivido — foi
 *  perder isso que motivou o pedido. */
export async function rodarBateria(
  tetoMb: number | null,
  aoAndar: (a: AndamentoBateria) => void,
  querParar: () => boolean = () => false,
  /** Quem roda um modelo. Trocável para que a regra de parada possa ser
   *  verificada sem baixar meio giga por asserção: o que se quer provar aqui é
   *  o que a bateria FAZ com um resultado ruim, e isso não depende de o
   *  resultado ruim ter vindo de um modelo de verdade. */
  executar: (p: ProvaModelo, aoAndar: (t: string) => void) => Promise<ResultadoProva> = rodarProva,
): Promise<PlanoBateria> {
  const plano = planejarBateria(tetoMb);
  if (plano.recusa) return plano;
  const limiteMb = plano.limiteMb!;
  const total = plano.passos.length;

  /** Condena o que sobrou sem tentar. Usado quando o motivo da parada vale
   *  também para os seguintes — que, por construção, são mais pesados. */
  const condenarRestante = (desde: number, estado: "naocabe" | "naotestado", motivo: string) => {
    for (let j = desde; j < total; j++) {
      const passo = plano.passos[j]!;
      if (passo.recurso) marcarVeredito(passo.recurso, { estado, motivo }, tetoMb);
      aoAndar({ indice: j, total, prova: passo.prova, recurso: passo.recurso, texto: motivo, estado });
    }
  };

  for (let i = 0; i < total; i++) {
    const passo = plano.passos[i]!;
    const { prova, recurso } = passo;

    if (querParar()) {
      condenarRestante(i, "naotestado", "a bateria foi interrompida antes de chegar aqui");
      return plano;
    }

    if (!passo.rodar) {
      if (recurso) marcarVeredito(recurso, { estado: "naocabe", motivo: passo.motivo }, tetoMb);
      aoAndar({ indice: i, total, prova, recurso, texto: passo.motivo, estado: "naocabe" });
      continue;
    }

    aoAndar({ indice: i, total, prova, recurso, texto: "baixando e carregando…", estado: "andando" });
    const r = await executar(prova, (t) => {
      aoAndar({ indice: i, total, prova, recurso, texto: t, estado: "andando" });
    });

    if (r.estado === "erro") {
      const memoria = pareceFaltaDeMemoria(r.detalhe);
      const motivo = memoria
        ? `não coube aqui: parou aos ${r.custoMb} MB — ${r.detalhe}`
        : `o teste não chegou a medir: ${r.detalhe}`;
      if (recurso) {
        marcarVeredito(recurso, { estado: memoria ? "naocabe" : "naotestado", motivo }, tetoMb);
      }
      aoAndar({
        indice: i, total, prova, recurso, texto: motivo,
        estado: memoria ? "naocabe" : "naotestado",
      });
      // Parar aqui é o ponto do desenho: o que vem depois é mais pesado.
      condenarRestante(
        i + 1,
        memoria ? "naocabe" : "naotestado",
        memoria
          ? `mais pesado que "${prova.nome}", que já não coube neste aparelho`
          : `a bateria parou em "${prova.nome}" antes de chegar aqui`,
      );
      return plano;
    }

    const cabe = r.custoMb <= limiteMb;
    const motivo = cabe
      ? `ocupou ${r.custoMb} MB neste aparelho, dentro dos ${limiteMb} MB de margem`
      : `ocupou ${r.custoMb} MB, acima dos ${limiteMb} MB de margem deste aparelho`;
    if (recurso) {
      marcarVeredito(
        recurso,
        { estado: cabe ? "cabe" : "naocabe", motivo, custoMb: r.custoMb },
        tetoMb,
      );
    }
    aoAndar({
      indice: i, total, prova, recurso,
      texto: `${motivo} · ${r.totalSeg}s`,
      estado: cabe ? "cabe" : "naocabe",
    });
    if (!cabe) {
      condenarRestante(
        i + 1,
        "naocabe",
        `mais pesado que "${prova.nome}", que já passou da margem deste aparelho`,
      );
      return plano;
    }
  }
  return plano;
}

/** Converte o rastro deixado por quem fechou o app em veredito.
 *
 *  É o único caso em que o resultado não vem de um retorno de função: o teste
 *  que mata o processo é justamente o que não consegue se reportar. Aqui o
 *  rastro vale como medição — e condena junto tudo que é mais pesado, pela
 *  mesma inferência da parada na primeira falha.
 *
 *  Devolve os recursos que foram condenados, para a tela poder dizer quais. */
export function condenarQuemDerrubou(
  vitima: { id?: string; nome: string; dtype: string },
  tetoMb: number | null,
): RecursoId[] {
  const ordem = [...PROVAS].sort((a, b) => custoConhecidoMb(a) - custoConhecidoMb(b));
  const pos = ordem.findIndex(
    (p) => (vitima.id ? p.id === vitima.id : p.nome === vitima.nome) && p.dtype === vitima.dtype,
  );
  if (pos < 0) return [];
  const condenados: RecursoId[] = [];
  for (let i = pos; i < ordem.length; i++) {
    const recurso = recursoDaProva(ordem[i]!);
    if (!recurso) continue;
    const motivo =
      i === pos
        ? `o app fechou enquanto este modelo era testado — isso conta como resultado`
        : `mais pesado que "${vitima.nome}", que fechou o app neste aparelho`;
    marcarVeredito(recurso, { estado: "naocabe", motivo }, tetoMb);
    condenados.push(recurso);
  }
  return condenados;
}

/** Ponte para os testes, no mesmo padrão de `window.__wuweiInferencia`.
 *
 *  O plano e a classificação da falha são funções puras; perguntar direto a
 *  elas prova o que a tela sozinha não prova — e não custa meio giga de
 *  download por asserção. */
declare global {
  interface Window {
    __wuweiProvas: {
      PROVAS: typeof PROVAS;
      FOLGA_DO_TETO: number;
      custoConhecidoMb: typeof custoConhecidoMb;
      planejarBateria: typeof planejarBateria;
      rodarBateria: typeof rodarBateria;
      recursoDaProva: typeof recursoDaProva;
      pareceFaltaDeMemoria: typeof pareceFaltaDeMemoria;
      condenarQuemDerrubou: typeof condenarQuemDerrubou;
    };
  }
}

if (typeof window !== "undefined") {
  window.__wuweiProvas = {
    PROVAS,
    FOLGA_DO_TETO,
    custoConhecidoMb,
    planejarBateria,
    rodarBateria,
    recursoDaProva,
    pareceFaltaDeMemoria,
    condenarQuemDerrubou,
  };
}

/** Manda o resultado para a máquina que está acompanhando.
 *
 *  Sem isto, quem testa no aparelho precisa mandar captura de tela de cada
 *  linha — e captura não é dado: não soma, não ordena, não compara com a
 *  medição anterior. Falha em silêncio de propósito: o teste vale mesmo que a
 *  coleta não chegue, e um erro aqui não pode derrubar a bateria. */
export async function enviarResultado(
  destino: string,
  dado: Record<string, unknown>,
): Promise<boolean> {
  try {
    await fetch(`${destino.replace(/\/$/, "")}/resultado`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        ...dado,
        onde: "app-webview",
        ua: navigator.userAgent,
        nucleos: navigator.hardwareConcurrency ?? null,
      }),
    });
    return true;
  } catch {
    return false;
  }
}
